import { useAuthStore } from "@/stores/useAuthStore"
import { useCallback, useEffect, useState } from "react"

export function postUseMineListScope(hasPermission: (controller: string, action: string) => boolean): boolean {
  return hasPermission("post", "findMine") && !hasPermission("post", "find")
}

export function postFetchPostByIdUseMine(hasPermission: (controller: string, action: string) => boolean): boolean {
  return hasPermission("post", "findOneMine") && !hasPermission("post", "findOne")
}

export const useCheckPermission = () => {
  const permissions = useAuthStore(state => state.permissions)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check if permissions are loaded from store
    if (permissions) {
      setIsLoading(false)
    } else {
      // If store is null, check if we're authenticated (have auth_token)
      // If authenticated but permissions not loaded yet, wait for async load
      if (typeof window !== "undefined") {
        const authToken = localStorage.getItem("auth_token")
        if (!authToken) {
          // Not authenticated, no need to wait
          setIsLoading(false)
        } else {
          // Authenticated but permissions not loaded yet, wait with timeout
          const timer = setTimeout(() => {
            setIsLoading(false)
          }, 100)
          return () => clearTimeout(timer)
        }
      } else {
        setIsLoading(false)
      }
    }
  }, [permissions])

  const hasPermission = useCallback(
    (controller: string, action: string): boolean => {
      if (!permissions) return false
      return !!permissions[controller]?.[action]?.enabled
    },
    [permissions],
  )

  // Helper for hooks to check before API calls
  const checkBeforeAction = useCallback(
    (controller: string, action: string, onSuccess: () => void | Promise<void>, onDenied?: () => void): boolean => {
      if (!hasPermission(controller, action)) {
        onDenied?.()
        return false
      }
      onSuccess()
      return true
    },
    [hasPermission],
  )

  return { hasPermission, permissions, isLoading, checkBeforeAction }
}
